import { FC } from 'react';
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form';
import useReactRouter from 'use-react-router';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from 'jbc-front/components/Button';

import { useEmployeeMutation as useMutation } from '@jbc-year-end-adj/2024/hooks/graphql/useMutation';
import { Question } from '@jbc-year-end-adj/2024/features/questions/types';
import { Spouse as Template } from '@jbc-year-end-adj/2024/features/questions/templates/Spouse';
import { ButtonContainer } from '@jbc-year-end-adj/2024/features/questions/components/ButtonContainer';
import { Section } from '@jbc-year-end-adj/2024/components/ui/Section';
import { FormSection } from '@jbc-year-end-adj/2024/components/form/FormSection';
import { Row } from '@jbc-year-end-adj/2024/components/form/Row';
import { Input } from '@jbc-year-end-adj/2024/components/form/Input';
import { Radio } from '@jbc-year-end-adj/2024/components/form/Radio';
import { DateField } from '@jbc-year-end-adj/2024/components/react-hook-form/DateField';

import { schema, generateDefaultValues, Schema } from './schema';
import { ThisYear } from './ThisYear';
import { NextYear } from './NextYear';
import { ANSWER_SPOUSE, convertFormDataToQueryVariables } from './mutation';
import { QueryResult } from './query';

type SpouseProps = {
  question: Question;
  profile: Exclude<QueryResult['request']['profile'], undefined>;
  handicapImageSetting: 'none' | 'optional' | 'required';
  nonResidentAndStudyAbroadImageSetting: 'none' | 'optional' | 'required';
};

export const Form: FC<SpouseProps> = ({ question, profile, handicapImageSetting, nonResidentAndStudyAbroadImageSetting }) => {
  const { history } = useReactRouter();

  const isSameHousehold = profile.spouseLivingStatus.isSameHousehold;
  const handicapImageRequired = handicapImageSetting === 'required';
  const nonResidentAndStudyAbroadImageRequired = nonResidentAndStudyAbroadImageSetting === 'required';

  const methods = useForm({
    defaultValues: generateDefaultValues(profile.spouse, !!profile.hasSpouseDeduction),
    resolver: yupResolver(schema(handicapImageRequired, nonResidentAndStudyAbroadImageRequired, isSameHousehold))
  });

  const {
    handleSubmit,
    formState: { errors },
    register,
    watch,
    setValue
  } = methods;

  const thisYearDependentTaxLaw = watch('spouse.thisYear.dependentTaxLaw');
  const nextYearDependentTaxLaw = watch('spouse.nextYear.dependentTaxLaw');

  const hasSpouseDeduction = watch('hasSpouseDeduction');

  const handleClickHasNotSpouseDeduction = () => {
    setValue('hasSpouseDeduction', false);

    if (!isSameHousehold) {
      handleSubmit(onSubmit)();
    }
  };

  const [update, { loading }] = useMutation(ANSWER_SPOUSE);

  const onSubmit: SubmitHandler<Schema> = async (data: Schema) => {
    const variables = convertFormDataToQueryVariables(data, isSameHousehold);
    await update({ variables });

    history.push('/employee_input');
  };

  return (
    <FormProvider {...methods}>
      <Template>
        <Template.CheckHasSpouseDeduction question={question}>
          <ButtonContainer>
            <Button huge primary={hasSpouseDeduction} secondary={!hasSpouseDeduction} onClick={() => setValue('hasSpouseDeduction', true)}>
              はい
            </Button>
            <Button huge primary={!hasSpouseDeduction} secondary={hasSpouseDeduction} onClick={() => handleClickHasNotSpouseDeduction()}>
              いいえ
            </Button>
          </ButtonContainer>
        </Template.CheckHasSpouseDeduction>

        {(hasSpouseDeduction || isSameHousehold) && (
          <Template.DetailInput initiallyOpen={true}>
            <Section>
              <Section.Header>配偶者の基本情報</Section.Header>
              <Section.Body>
                <FormSection>
                  <Row>
                    <Input
                      label="姓"
                      required
                      {...register('spouse.lastName')}
                      error={errors.spouse?.lastName?.message}
                      isError={!!errors.spouse?.lastName}
                    />
                    <Input
                      label="名"
                      required
                      {...register('spouse.firstName')}
                      error={errors.spouse?.firstName?.message}
                      isError={!!errors.spouse?.firstName}
                    />
                  </Row>

                  <Row>
                    <Input
                      label="姓（カナ）"
                      required
                      {...register('spouse.lastNameKana')}
                      error={errors.spouse?.lastNameKana?.message}
                      isError={!!errors.spouse?.lastNameKana}
                    />
                    <Input
                      label="名（カナ）"
                      required
                      {...register('spouse.firstNameKana')}
                      error={errors.spouse?.firstNameKana?.message}
                      isError={!!errors.spouse?.firstNameKana}
                    />
                  </Row>

                  <Radio.Container label="性別" required>
                    <Radio value="male" {...register('spouse.gender')}>
                      男性
                    </Radio>
                    <Radio value="female" {...register('spouse.gender')}>
                      女性
                    </Radio>
                  </Radio.Container>

                  <DateField
                    label="生年月日"
                    required
                    name="spouse.birthday"
                    error={errors.spouse?.birthday?.message}
                    note="例）1981/01/23、S56.1.23"
                  />
                </FormSection>
              </Section.Body>
            </Section>

            <ThisYear isSameHousehold={isSameHousehold} />
            <NextYear />

            {(thisYearDependentTaxLaw === 'true' || nextYearDependentTaxLaw === 'true') && (
              <>
                <DateField
                  label="源泉控除対象配偶者になった日"
                  note="源泉控除対象配偶者の場合のみ記入してください"
                  name="spouse.dependentFrom"
                  error={errors.spouse?.dependentFrom?.message}
                />

                <Input
                  label="源泉控除対象配偶者になった理由"
                  note="源泉控除対象配偶者の場合のみ記入してください"
                  {...register('spouse.dependentReason')}
                />

                <DateField label="死亡日" name="spouse.diedOn" error={errors.spouse?.diedOn?.message} />
              </>
            )}

            <ButtonContainer>
              <Button primary onClick={handleSubmit(onSubmit)} disabled={loading}>
                次へ
              </Button>
            </ButtonContainer>
          </Template.DetailInput>
        )}
      </Template>
    </FormProvider>
  );
};
